/**
 * DRI Insights utilities.
 * Business unit abbreviations, tier calculation, and pure deriving functions
 * (give them data, get derived data back). Used by DRIInsights.tsx useMemos.
 */

import { BU_ABBREVIATION_MAP } from '../constants/driInsightsConstants';
import type {
  UserDRIInsight,
  EmployeeSummary,
  DRIInsightDetail,
  EmployeeSummaryProductDetail,
  CertificationRecord,
  SkillBadgeRecord,
  IndividualDetailsRow,
  DRIValueType,
  EmployeeTrainingSummary,
  UnregisteredEmployeeRow,
} from '../types/driInsights';

/** Column filter state shape (min/max or selectedValues). */
export type ColumnFilterState = Record<string, {
  selectedValues?: string[];
  min?: number | null;
  max?: number | null;
  startDate?: Date | null;
  endDate?: Date | null;
}>;

/** Grid row: one employee with products keyed by product name. */
export interface GridRow {
  employee: EmployeeSummary;
  products: Record<string, EmployeeSummaryProductDetail>;
}

/** Individual details row: one employee-product pair (flat). */
export interface IndividualDetailRowFlat {
  employee: EmployeeSummary;
  product: EmployeeSummaryProductDetail;
}

/**
 * Get Business Unit abbreviation for display (e.g. "Delivery | Cloud Engineering" -> "CE").
 */
export const getBUAbbreviation = (buName: string): string => {
  return BU_ABBREVIATION_MAP[buName] ?? buName;
};

/**
 * Calculate DRI tier from score: 1 (>=50), 2 (>=35), 3 (>=20), 4 (<20).
 */
export const calculateTier = (driScore: number): number => {
  if (driScore >= 50) return 1;
  if (driScore >= 35) return 2;
  if (driScore >= 20) return 3;
  return 4;
};

/**
 * Get best (lowest) tier for an employee across all products.
 */
export const getBestTierForEmployee = (productDetails: Array<{ total_dri: number }>): number => {
  if (!productDetails || productDetails.length === 0) return 4;
  const tiers = productDetails.map((product) => calculateTier(product.total_dri ?? 0));
  return Math.min(...tiers);
};

/** Default certification trend month range: last 12 months. */
export interface DefaultCertificationMonths {
  startMonth: { year: number; month: number };
  endMonth: { year: number; month: number };
}

/**
 * Default month range for certification trend (current month as end, 12 months back as start).
 * Month is 1-based (1–12).
 */
export const getDefaultCertificationMonths = (): DefaultCertificationMonths => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1; // 1-based month
  const endMonth = { year: currentYear, month: currentMonth };
  let startYear = currentYear;
  let startMonthNum = currentMonth - 11;
  if (startMonthNum <= 0) {
    startMonthNum += 12;
    startYear -= 1;
  }
  const startMonth = { year: startYear, month: startMonthNum };
  return { startMonth, endMonth };
};

// ---------------------------------------------------------------------------
// Pure deriving functions: pass data in, get derived data out. Used by DRIInsights.
// ---------------------------------------------------------------------------

export function computeTotalActiveEmployees(driInsights: unknown): number {
  if (!driInsights || Array.isArray(driInsights)) return 0;
  return (driInsights as { total_active_employees?: number }).total_active_employees ?? 0;
}

export function computeRawData(driInsights: unknown): UserDRIInsight[] {
  if (!driInsights) return [];
  if (Array.isArray(driInsights)) return driInsights as UserDRIInsight[];
  return (driInsights as { dri_insights?: UserDRIInsight[] }).dri_insights ?? [];
}

export function computeAllProducts(
  rawData: UserDRIInsight[],
  filterOptionsProducts?: string[] | null,
  options?: { skipFilterOptionsProducts?: boolean }
): string[] {
  if (
    !options?.skipFilterOptionsProducts &&
    filterOptionsProducts &&
    filterOptionsProducts.length > 0
  ) {
    return [...filterOptionsProducts].sort();
  }
  const productSet = new Set<string>();
  rawData.forEach((user: UserDRIInsight) => {
    user.dri_details.forEach((detail) => {
      if (detail.product_name) productSet.add(detail.product_name);
    });
  });
  return Array.from(productSet).sort();
}

export function computeOptionalProducts(allProducts: string[], assignedProducts: string[]): string[] {
  const assignedSet = new Set(assignedProducts);
  return allProducts.filter((p) => !assignedSet.has(p));
}

export function computeAvailableProducts(assignedProducts: string[], optionalProducts: string[]): string[] {
  return [...assignedProducts, ...optionalProducts];
}

export function computeFilteredData(
  rawData: UserDRIInsight[],
  activeProducts: string[] | undefined
): UserDRIInsight[] {
  if (!activeProducts || activeProducts.length === 0) return rawData;
  return rawData
    .map((user: UserDRIInsight) => {
      const filteredDetails = user.dri_details.filter(
        (d) => d.product_name && activeProducts.includes(d.product_name)
      );
      const averageDRI =
        filteredDetails.length > 0
          ? filteredDetails.reduce((sum, d) => sum + (d.total_dri || 0), 0) / filteredDetails.length
          : user.average_dri || 0;
      return {
        ...user,
        dri_details: filteredDetails,
        average_dri: averageDRI,
      };
    })
    .filter((u) => u.dri_details.length > 0);
}

export function computeCertificationsData(
  certificationsAndSkillBadgesData: { certifications?: Array<{ employee_name?: string; employee_email: string; certification_name?: string; certification_provider?: string; partner_name?: string; status?: string; cert_status?: string; issue_date?: string; expiry_date?: string; public_link?: string }> } | null | undefined,
  filteredData: UserDRIInsight[]
): CertificationRecord[] {
  if (!certificationsAndSkillBadgesData?.certifications) return [];
  const employeeNameMap = new Map<string, string>();
  filteredData.forEach((user: UserDRIInsight) => {
    if (user.employee_email && user.employee_name) employeeNameMap.set(user.employee_email, user.employee_name);
  });
  return certificationsAndSkillBadgesData.certifications.map((cert) => ({
    employee_name: cert.employee_name || employeeNameMap.get(cert.employee_email) || cert.employee_email,
    employee_email: cert.employee_email,
    certification_name: cert.certification_name ?? '',
    certification_provider: cert.certification_provider || cert.partner_name || '',
    status: cert.status || cert.cert_status || 'Unknown',
    issue_date: cert.issue_date ?? '',
    expiry_date: cert.expiry_date ?? '',
    public_link: cert.public_link ?? '',
  })) as CertificationRecord[];
}

export function computeSkillBadgesData(
  certificationsAndSkillBadgesData: { skillBadges?: Array<{ employee_name?: string; employee_email: string; quest_name?: string; activity_name?: string; quest_level?: string; skill_badge_level?: string; completion_date?: string; last_completion_date?: string }> } | null | undefined,
  filteredData: UserDRIInsight[]
): SkillBadgeRecord[] {
  if (!certificationsAndSkillBadgesData?.skillBadges) return [];
  const employeeNameMap = new Map<string, string>();
  filteredData.forEach((user: UserDRIInsight) => {
    if (user.employee_email && user.employee_name) employeeNameMap.set(user.employee_email, user.employee_name);
  });
  return certificationsAndSkillBadgesData.skillBadges.map((badge) => ({
    employee_name: badge.employee_name || employeeNameMap.get(badge.employee_email) || badge.employee_email,
    employee_email: badge.employee_email,
    quest_name: badge.quest_name || badge.activity_name || '',
    quest_level: badge.quest_level || badge.skill_badge_level || '',
    completion_date: badge.completion_date || badge.last_completion_date || '',
  })) as SkillBadgeRecord[];
}

export function computeEmployeeTrainingsSummaryData(
  filteredData: UserDRIInsight[],
  certificationsAndSkillBadgesData: { certifications?: Array<{ employee_email?: string }>; skillBadges?: Array<{ employee_email?: string }> } | null | undefined
): EmployeeTrainingSummary[] {
  if (!filteredData) return [];
  const certCounts = new Map<string, number>();
  const badgeCounts = new Map<string, number>();
  if (certificationsAndSkillBadgesData?.certifications) {
    certificationsAndSkillBadgesData.certifications.forEach((c: { employee_email?: string }) => {
      if (c.employee_email) certCounts.set(c.employee_email, (certCounts.get(c.employee_email) || 0) + 1);
    });
  }
  if (certificationsAndSkillBadgesData?.skillBadges) {
    certificationsAndSkillBadgesData.skillBadges.forEach((b: { employee_email?: string }) => {
      if (b.employee_email) badgeCounts.set(b.employee_email, (badgeCounts.get(b.employee_email) || 0) + 1);
    });
  }
  return filteredData.map((user: UserDRIInsight) => {
    const highestDri =
      user.dri_details?.length > 0 ? Math.max(...user.dri_details.map((d) => d.total_dri || 0)) : 0;
    return {
      employee_email: user.employee_email,
      total_certifications: certCounts.get(user.employee_email) || 0,
      total_skill_badges: badgeCounts.get(user.employee_email) || 0,
      dri_tier: calculateTier(highestDri),
      dri_score_avg: user.average_dri || 0,
      dri_score_highest: highestDri,
      is_registered: user.is_registered === true,
    };
  });
}

/**
 * Map unregistered employees to the same row shape as {@link EmployeeTrainingSummary} for a unified table.
 * Skips emails already present in `registeredEmails` (registered wins).
 */
export function mapUnregisteredEmployeesToTrainingSummaryRows(
  unregisteredEmployees: UnregisteredEmployeeRow[],
  certificationsData: CertificationRecord[],
  skillBadgesData: SkillBadgeRecord[],
  registeredEmails: Set<string>
): EmployeeTrainingSummary[] {
  if (!unregisteredEmployees?.length) return [];
  return unregisteredEmployees
    .filter((u) => u.employee_email && !registeredEmails.has(u.employee_email))
    .map((u) => ({
      employee_email: u.employee_email,
      total_certifications: certificationsData.filter((c) => c.employee_email === u.employee_email).length,
      total_skill_badges: skillBadgesData.filter((b) => b.employee_email === u.employee_email).length,
      dri_tier: 0,
      dri_score_avg: 0,
      dri_score_highest: 0,
      is_registered: false,
    }));
}

export function computeEmployeeSummaryData(filteredData: UserDRIInsight[]): EmployeeSummary[] {
  return filteredData.map((user: UserDRIInsight) => {
    const latestEntriesByProduct = new Map<string, DRIInsightDetail>();
    user.dri_details.forEach((detail) => {
      if (!detail.product_name) return;
      const productName = detail.product_name;
      const existing = latestEntriesByProduct.get(productName);
      if (!existing) {
        latestEntriesByProduct.set(productName, detail);
      } else {
        const existingDate = existing.dri_report_received_date ? new Date(existing.dri_report_received_date) : null;
        const currentDate = detail.dri_report_received_date ? new Date(detail.dri_report_received_date) : null;
        if (currentDate && (!existingDate || currentDate > existingDate)) {
          latestEntriesByProduct.set(productName, detail);
        }
      }
    });
    const latestDriDetails = Array.from(latestEntriesByProduct.values());
    const averageDRI =
      user.average_dri !== undefined
        ? user.average_dri
        : latestDriDetails.length > 0
          ? latestDriDetails.reduce((sum, d) => sum + (d.total_dri || 0), 0) / latestDriDetails.length
          : 0;
    const driBreakdownTotals = latestDriDetails.reduce(
      (acc, d) => {
        acc.technical_certification += d.dri_through_technical_certification || 0;
        acc.labs += d.dri_through_labs || 0;
        acc.project_deployed += d.dri_through_project_deployed || 0;
        acc.skill_badges += d.dri_through_skill_badges || 0;
        acc.other_cloud_experience += d.dri_through_other_cloud_experience || 0;
        acc.technical_training += d.dri_through_technical_training || 0;
        return acc;
      },
      {
        technical_certification: 0,
        labs: 0,
        project_deployed: 0,
        skill_badges: 0,
        other_cloud_experience: 0,
        technical_training: 0,
      } as EmployeeSummary['dri_breakdown_totals']
    );
    const productDetails = latestDriDetails.map((d) => ({
      product_name: d.product_name,
      total_dri: d.total_dri || 0,
      dri_through_technical_certification: d.dri_through_technical_certification || 0,
      dri_through_labs: d.dri_through_labs || 0,
      dri_through_project_deployed: d.dri_through_project_deployed || 0,
      dri_through_skill_badges: d.dri_through_skill_badges || 0,
      dri_through_other_cloud_experience: d.dri_through_other_cloud_experience || 0,
      dri_through_technical_training: d.dri_through_technical_training || 0,
      dri_report_received_date: d.dri_report_received_date,
    }));
    return {
      member_name: user.employee_name,
      member_email: user.employee_email,
      business_unit: user.business_unit,
      sub_business_unit: user.sub_business_unit || '-',
      average_dri: parseFloat(averageDRI.toFixed(2)),
      dri_breakdown_totals: driBreakdownTotals,
      drp_id: user.drp_id,
      product_details: productDetails,
    };
  });
}

export function computeIndividualDetailsRows(employeeSummaryData: EmployeeSummary[]): IndividualDetailRowFlat[] {
  const rows: IndividualDetailRowFlat[] = [];
  const emptyProduct: EmployeeSummaryProductDetail = {
    product_name: '-',
    total_dri: 0,
    dri_through_technical_certification: 0,
    dri_through_labs: 0,
    dri_through_project_deployed: 0,
    dri_through_skill_badges: 0,
    dri_through_other_cloud_experience: 0,
    dri_through_technical_training: 0,
  };
  employeeSummaryData.forEach((employee) => {
    if (employee.product_details && employee.product_details.length > 0) {
      employee.product_details.forEach((product) => {
        rows.push({ employee, product });
      });
    } else {
      rows.push({ employee, product: emptyProduct });
    }
  });
  return rows;
}

export function computeAvailableBUsFromEmployeeSummary(employeeSummaryData: EmployeeSummary[]): string[] {
  if (!employeeSummaryData?.length) return [];
  const buSet = new Set<string>();
  employeeSummaryData.forEach((e) => {
    if (e.business_unit) buSet.add(e.business_unit);
  });
  return Array.from(buSet).sort();
}

export function computeAvailableProductsForTierDistribution(employeeSummaryData: EmployeeSummary[]): string[] {
  if (!employeeSummaryData?.length) return [];
  const productSet = new Set<string>();
  employeeSummaryData.forEach((employee) => {
    employee.product_details?.forEach((product) => {
      if (product.product_name && product.product_name !== '-') productSet.add(product.product_name);
    });
  });
  return Array.from(productSet).sort();
}

export function computeCertificationMetrics(
  certificationsData: CertificationRecord[],
  employeeSummaryData: EmployeeSummary[] | undefined
): { activeCertifications: number; expiringCertifications: number; employeesWithoutCertifications: number } {
  if (!certificationsData?.length) {
    return {
      activeCertifications: 0,
      expiringCertifications: 0,
      employeesWithoutCertifications: employeeSummaryData?.length ?? 0,
    };
  }
  const today = new Date();
  const ninetyDaysFromNow = new Date();
  ninetyDaysFromNow.setDate(today.getDate() + 90);
  const activeCertifications = certificationsData.filter((cert) => {
    const status = (cert.status ?? '').toLowerCase();
    const expiryDate = cert.expiry_date ? new Date(cert.expiry_date) : null;
    if (status.includes('active')) return true;
    if (!expiryDate) return true;
    return expiryDate >= today;
  }).length;
  const expiringCertifications = certificationsData.filter((cert) => {
    if (!cert.expiry_date) return false;
    const expiryDate = new Date(cert.expiry_date);
    return expiryDate >= today && expiryDate <= ninetyDaysFromNow;
  }).length;
  const employeesWithCerts = new Set(certificationsData.map((c) => c.employee_email));
  const employeesWithoutCertifications = (employeeSummaryData ?? []).filter(
    (e) => !employeesWithCerts.has(e.member_email)
  ).length;
  return { activeCertifications, expiringCertifications, employeesWithoutCertifications };
}

export function computeSummaryMetrics(
  employeeSummaryData: EmployeeSummary[],
  totalActiveEmployees: number
): {
  totalRecords: number;
  tierDistribution: { tier1: number; tier2: number; tier3: number; tier4: number };
  totalActiveEmployees: number;
  driActiveEmployees: number;
  driInactiveEmployees: number;
  topThreeProducts: Array<{ productName: string; employeeCount: number; maxAverageDRI: number; averageDRI: number }>;
  profilesWithNoProjectsDRI: number;
  profilesWithNoCertificationDRI: number;
  profilesWithNoTechnicalSkillDRI: number;
} {
  if (!employeeSummaryData?.length) {
    return {
      totalRecords: 0,
      tierDistribution: { tier1: 0, tier2: 0, tier3: 0, tier4: 0 },
      totalActiveEmployees,
      driActiveEmployees: 0,
      driInactiveEmployees: totalActiveEmployees,
      topThreeProducts: [],
      profilesWithNoProjectsDRI: 0,
      profilesWithNoCertificationDRI: 0,
      profilesWithNoTechnicalSkillDRI: 0,
    };
  }
  let totalRecords = 0;
  employeeSummaryData.forEach((employee) => {
    if (employee.product_details?.length) totalRecords += employee.product_details.length;
    else totalRecords += 1;
  });
  const tierDistribution = { tier1: 0, tier2: 0, tier3: 0, tier4: 0 };
  employeeSummaryData.forEach((employee) => {
    const bestTier = getBestTierForEmployee(employee.product_details || []);
    if (bestTier === 1) tierDistribution.tier1++;
    else if (bestTier === 2) tierDistribution.tier2++;
    else if (bestTier === 3) tierDistribution.tier3++;
    else tierDistribution.tier4++;
  });
  const driActiveEmployees = employeeSummaryData.length;
  const driInactiveEmployees = Math.max(0, totalActiveEmployees - driActiveEmployees);
  let profilesWithNoProjectsDRI = 0;
  let profilesWithNoCertificationDRI = 0;
  let profilesWithNoTechnicalSkillDRI = 0;
  employeeSummaryData.forEach((employee) => {
    const totals = employee.dri_breakdown_totals ?? {
      technical_certification: 0,
      labs: 0,
      project_deployed: 0,
      skill_badges: 0,
      other_cloud_experience: 0,
      technical_training: 0,
    };
    if (totals.project_deployed === 0) profilesWithNoProjectsDRI++;
    if (totals.technical_certification === 0) profilesWithNoCertificationDRI++;
    if (totals.skill_badges === 0) profilesWithNoTechnicalSkillDRI++;
  });
  const productStats = new Map<
    string,
    { employeeCount: number; maxAverageDRI: number; totalDRI: number; employeeCountWithDRI: number }
  >();
  employeeSummaryData.forEach((employee) => {
    employee.product_details?.forEach((product) => {
      if (product.product_name && product.product_name !== '-') {
        const existing = productStats.get(product.product_name) ?? {
          employeeCount: 0,
          maxAverageDRI: 0,
          totalDRI: 0,
          employeeCountWithDRI: 0,
        };
        existing.employeeCount += 1;
        if (product.total_dri > 0) {
          existing.employeeCountWithDRI += 1;
          existing.totalDRI += product.total_dri;
          if (product.total_dri > existing.maxAverageDRI) existing.maxAverageDRI = product.total_dri;
        }
        productStats.set(product.product_name, existing);
      }
    });
  });
  const sortedProducts = Array.from(productStats.entries())
    .map(([productName, stats]) => ({
      productName,
      employeeCount: stats.employeeCount,
      maxAverageDRI: stats.maxAverageDRI,
      averageDRI: stats.employeeCount ? stats.totalDRI / stats.employeeCount : 0,
    }))
    .sort((a, b) => (b.averageDRI !== a.averageDRI ? b.averageDRI - a.averageDRI : b.employeeCount - a.employeeCount))
    .slice(0, 3);
  return {
    totalRecords,
    tierDistribution,
    totalActiveEmployees,
    driActiveEmployees,
    driInactiveEmployees,
    topThreeProducts: sortedProducts,
    profilesWithNoProjectsDRI,
    profilesWithNoCertificationDRI,
    profilesWithNoTechnicalSkillDRI,
  };
}

export function computeEmployeesByTierData(
  filteredData: UserDRIInsight[],
  viewMode: 'product' | 'bu'
): Array<{ name: string; tier1: number; tier2: number; tier3: number; tier4: number }> {
  if (!filteredData?.length) return [];
  if (viewMode === 'product') {
    const productTiers: Record<string, { tier1: number; tier2: number; tier3: number; tier4: number }> = {};
    filteredData.forEach((user) => {
      user.dri_details?.forEach((detail) => {
        if (!productTiers[detail.product_name]) productTiers[detail.product_name] = { tier1: 0, tier2: 0, tier3: 0, tier4: 0 };
        const tier = calculateTier(detail.total_dri || 0);
        if (tier === 1) productTiers[detail.product_name].tier1++;
        else if (tier === 2) productTiers[detail.product_name].tier2++;
        else if (tier === 3) productTiers[detail.product_name].tier3++;
        else productTiers[detail.product_name].tier4++;
      });
    });
    return Object.entries(productTiers)
      .map(([product, tiers]) => ({ name: product, ...tiers }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
  const buTiers: Record<string, { tier1: number; tier2: number; tier3: number; tier4: number }> = {};
  filteredData.forEach((user) => {
    const bu = user.business_unit || 'Unknown';
    if (!buTiers[bu]) buTiers[bu] = { tier1: 0, tier2: 0, tier3: 0, tier4: 0 };
    const bestTier =
      user.dri_details?.length
        ? Math.min(...user.dri_details.map((d) => calculateTier(d.total_dri || 0)))
        : 4;
    if (bestTier === 1) buTiers[bu].tier1++;
    else if (bestTier === 2) buTiers[bu].tier2++;
    else if (bestTier === 3) buTiers[bu].tier3++;
    else buTiers[bu].tier4++;
  });
  return Object.entries(buTiers)
    .map(([bu, tiers]) => ({ name: bu, ...tiers }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function computeProductDriDataAll(filteredData: UserDRIInsight[]): Array<{
  product_name: string;
  average_dri: number;
  total_dri: number;
  employee_count: number;
  bu_count: number;
}> {
  const productMap = new Map<
    string,
    { product_name: string; total_dri: number; employee_count: number; bu_count: Set<string> }
  >();
  filteredData.forEach((user) => {
    user.dri_details.forEach((detail) => {
      const productName = detail.product_name;
      if (!productName) return;
      if (!productMap.has(productName)) {
        productMap.set(productName, { product_name: productName, total_dri: 0, employee_count: 0, bu_count: new Set() });
      }
      const p = productMap.get(productName)!;
      p.total_dri += detail.total_dri || 0;
      p.employee_count += 1;
      if (user.business_unit) p.bu_count.add(user.business_unit);
    });
  });
  return Array.from(productMap.values())
    .map((product) => ({
      product_name: product.product_name,
      average_dri: product.employee_count > 0 ? parseFloat((product.total_dri / product.employee_count).toFixed(2)) : 0,
      total_dri: product.total_dri,
      employee_count: product.employee_count,
      bu_count: product.bu_count.size,
    }))
    .sort((a, b) => b.average_dri - a.average_dri);
}

/** Tier distribution counts (tier1–tier4). */
export function computeFilteredTierDistribution(
  employeeSummaryData: EmployeeSummary[],
  selectedBUs: string[],
  selectedProducts: string[]
): { tier1: number; tier2: number; tier3: number; tier4: number } {
  if (!employeeSummaryData?.length) return { tier1: 0, tier2: 0, tier3: 0, tier4: 0 };
  let filtered = employeeSummaryData;
  if (selectedBUs.length > 0) {
    filtered = filtered.filter((e) => e.business_unit && selectedBUs.includes(e.business_unit));
  }
  if (selectedProducts.length > 0) {
    filtered = filtered.filter((e) => {
      if (!e.product_details?.length) return false;
      return e.product_details.some(
        (p) => p.product_name && p.product_name !== '-' && selectedProducts.includes(p.product_name)
      );
    });
  }
  const tierDistribution = { tier1: 0, tier2: 0, tier3: 0, tier4: 0 };
  filtered.forEach((employee) => {
    let productsToConsider = employee.product_details || [];
    if (selectedProducts.length > 0) {
      productsToConsider = productsToConsider.filter(
        (p) => p.product_name && p.product_name !== '-' && selectedProducts.includes(p.product_name)
      );
    }
    const bestTier = getBestTierForEmployee(productsToConsider);
    if (bestTier === 1) tierDistribution.tier1++;
    else if (bestTier === 2) tierDistribution.tier2++;
    else if (bestTier === 3) tierDistribution.tier3++;
    else tierDistribution.tier4++;
  });
  return tierDistribution;
}

/** Product DRI data filtered by selected BUs (for Average DRI bar chart). */
export function computeProductDriDataFilteredByBUs(
  employeeSummaryData: EmployeeSummary[],
  selectedBUs: string[]
): Array<{ product_name: string; average_dri: number; total_dri: number; employee_count: number; bu_count: number }> {
  if (!selectedBUs?.length || !employeeSummaryData?.length) return [];
  const filteredEmployees = employeeSummaryData.filter(
    (e) => e.business_unit && selectedBUs.includes(e.business_unit)
  );
  const productMap = new Map<
    string,
    { product_name: string; total_dri: number; employee_count: number; bu_count: Set<string> }
  >();
  filteredEmployees.forEach((employee) => {
    employee.product_details?.forEach((product) => {
      const productName = product.product_name;
      if (!productName || productName === '-') return;
      if (!productMap.has(productName)) {
        productMap.set(productName, { product_name: productName, total_dri: 0, employee_count: 0, bu_count: new Set() });
      }
      const p = productMap.get(productName)!;
      p.total_dri += product.total_dri || 0;
      p.employee_count += 1;
      if (employee.business_unit) p.bu_count.add(employee.business_unit);
    });
  });
  return Array.from(productMap.values())
    .map((product) => ({
      product_name: product.product_name,
      average_dri: product.employee_count > 0 ? parseFloat((product.total_dri / product.employee_count).toFixed(2)) : 0,
      total_dri: product.total_dri,
      employee_count: product.employee_count,
      bu_count: product.bu_count.size,
    }))
    .sort((a, b) => b.average_dri - a.average_dri);
}

/** DRI cell value for one product column (Individual Details / EmployeeWise grid). */
export function driValueForProductDetail(
  prod: EmployeeSummaryProductDetail | undefined,
  valueType: DRIValueType
): number {
  if (!prod) return 0;
  switch (valueType) {
    case 'total_dri':
      return prod.total_dri;
    case 'certification':
      return prod.dri_through_technical_certification;
    case 'labs':
      return prod.dri_through_labs;
    case 'projects':
      return prod.dri_through_project_deployed;
    case 'skill_badges':
      return prod.dri_through_skill_badges;
    case 'other_cloud':
      return prod.dri_through_other_cloud_experience;
    case 'training':
      return prod.dri_through_technical_training;
    default:
      return prod.total_dri;
  }
}

/** Ascending comparator for Individual Details rows (employee × product grid). */
export function compareIndividualDetailsRows(
  a: IndividualDetailsRow,
  b: IndividualDetailsRow,
  columnId: string,
  gridCellValueType: DRIValueType
): number {
  let valA: string | number;
  let valB: string | number;
  if (columnId.startsWith('product_')) {
    const productName = columnId.replace('product_', '');
    valA =
      a.products[productName] !== undefined
        ? driValueForProductDetail(a.products[productName], gridCellValueType)
        : -1;
    valB =
      b.products[productName] !== undefined
        ? driValueForProductDetail(b.products[productName], gridCellValueType)
        : -1;
  } else {
    const mappings: Record<string, keyof IndividualDetailsRow['employee']> = {
      individual_employee_name: 'member_name',
      individual_email: 'member_email',
      individual_bu: 'business_unit',
      individual_sub_bu: 'sub_business_unit',
    };
    const key = mappings[columnId];
    valA = (key ? a.employee[key] : '') as string | number;
    valB = (key ? b.employee[key] : '') as string | number;
  }
  if (valA < valB) return -1;
  if (valA > valB) return 1;
  return 0;
}
